import type {
  GuideLearningArea,
  GuideModuleSlug
} from "../types/guide-content.types.ts"
import {
  isGuideLessonAccessible,
  isGuideModuleAccessible
} from "./guide-permissions.ts"
import { getLessonsForModule } from "./lessons.ts"

/**
 * The areas a user actually thinks in — "Leads", not "the leads module".
 * Most areas wrap exactly one existing `GuideModule` 1:1 (Leads↔leads,
 * Projects↔projects, Documents↔files); "People" is the one area that
 * genuinely spans several separately-permissioned real pages
 * (Customers/Subcontractors/Team Management/Suppliers). "start-here" is
 * deliberately not one of these — it's the standalone Quick Start path,
 * reached directly, not through Browse by Area.
 *
 * There is no separate "Finance" area: Project Finance is the only finance
 * content the Guide has, and it belongs under Projects, not a top-level
 * area of its own — a bare `finance` `GuideModule` still exists (for the
 * legacy `/guide/academy/finance` deep link and any future *global* finance
 * content that isn't project-scoped), it's just never one of these browsable
 * areas.
 */
export const LEARNING_AREAS: GuideLearningArea[] = [
  {
    id: "sales",
    titleKey: "guide.areas.sales.title",
    descriptionKey: "guide.areas.sales.description",
    icon: "Handshake",
    moduleSlugs: ["leads"]
  },
  {
    id: "projects",
    titleKey: "guide.areas.projects.title",
    descriptionKey: "guide.areas.projects.description",
    icon: "Projects",
    moduleSlugs: ["projects"]
  },
  {
    id: "people",
    titleKey: "guide.areas.people.title",
    descriptionKey: "guide.areas.people.description",
    icon: "Users",
    moduleSlugs: ["customers", "subcontractors", "team-management", "suppliers"]
  },
  {
    id: "documents",
    titleKey: "guide.areas.documents.title",
    descriptionKey: "guide.areas.documents.description",
    icon: "Upload",
    moduleSlugs: ["files"]
  },
  {
    id: "communication",
    titleKey: "guide.areas.communication.title",
    descriptionKey: "guide.areas.communication.description",
    icon: "Bell",
    moduleSlugs: ["communication"]
  }
]

export const getLearningArea = (id: string): GuideLearningArea | null =>
  LEARNING_AREAS.find((area) => area.id === id) ?? null

export const findAreaForModule = (
  moduleSlug: GuideModuleSlug
): GuideLearningArea | null =>
  LEARNING_AREAS.find((area) => area.moduleSlugs.includes(moduleSlug)) ?? null

export const isLearningAreaAccessible = (
  area: GuideLearningArea,
  pages: readonly string[] | null | undefined
): boolean =>
  area.moduleSlugs.some((slug) => isGuideModuleAccessible(slug, pages))

/**
 * A Browse-by-Area card earns its place only when BOTH the user has
 * permission AND at least one lesson actually survives that permission
 * filter — a permitted-but-still-empty area (nothing authored for it yet)
 * is exactly as invisible as an unpermitted one. This is the one place that
 * decides "does this card exist at all"; a lesson count of zero must never
 * render as a "coming soon" destination anywhere in the Guide.
 */
export const getAccessibleLearningAreas = (
  pages: readonly string[] | null | undefined,
  codes: unknown
): GuideLearningArea[] =>
  LEARNING_AREAS.filter(
    (area) =>
      isLearningAreaAccessible(area, pages) &&
      getAccessibleLessonsForArea(area, pages, codes).length > 0
  )

/** Every lesson across the area's modules, in module order then lesson
 * order — the "Recommended Path", unfiltered by permission. Takes just
 * `moduleSlugs` (not a full `GuideLearningArea`) so a single-module
 * fallback — the "start-here" deep link isn't one of the six real areas —
 * can be passed without forcing extra fields onto it. */
export const getLessonsForArea = (area: { moduleSlugs: GuideModuleSlug[] }) =>
  area.moduleSlugs.flatMap((slug) => getLessonsForModule(slug))

/** The same list, narrowed to lessons this user can actually open — what
 * every count, progress fraction and "Recommended Path" render should use. */
export const getAccessibleLessonsForArea = (
  area: { moduleSlugs: GuideModuleSlug[] },
  pages: readonly string[] | null | undefined,
  codes: unknown
) =>
  getLessonsForArea(area).filter((lesson) =>
    isGuideLessonAccessible(lesson, pages, codes)
  )

export interface GuideAreaProgress {
  completed: number
  total: number
}

export const getAreaProgress = (
  lessons: { id: string }[],
  completedLessonIds: readonly string[]
): GuideAreaProgress => ({
  completed: lessons.filter((lesson) => completedLessonIds.includes(lesson.id))
    .length,
  total: lessons.length
})
